/**
 * Per-widget component breakdown of one commit, read off React's own fiber
 * tree.
 *
 * The tracker's per-widget commit total says a widget costs 100 ms a commit;
 * it can't say which component inside it. The profiling build stamps every
 * fiber with `actualDuration` / `actualStartTime` on each render, so walking
 * the widget's Profiler subtree right after a commit (from its onRender, while
 * the stamps still belong to that commit) and charging each fiber its self
 * time (own duration minus its children's) names the component — even in a
 * minified bundle, where the source snippet of the component function is what
 * maps a two-letter name back to a file.
 *
 * Debug-only: everything here reads undocumented `__reactFiber$…` internals
 * and returns `null` rather than throw when they aren't where expected.
 */

export type ComponentCost = {
  name: string
  /** First ~90 chars of the component's source, whitespace-collapsed. */
  snippet: string
  /** Fiber instances of this component under the widget. */
  instances: number
  /** Instances whose newest render belongs to the profiled commit. */
  inCommit: number
  /** Self ms summed over instances that rendered in the profiled commit. */
  selfMs: number
  maxSelfMs: number
}

export type WidgetFiberProfile = {
  widgetId: string
  fibers: number
  /** The Profiler's own actualDuration for the profiled commit. */
  commitMs: number
  /** Top components by self ms in that commit. */
  top: ComponentCost[]
}

type Fiber = {
  tag: number
  type: unknown
  return: Fiber | null
  child: Fiber | null
  sibling: Fiber | null
  alternate: Fiber | null
  memoizedProps: { id?: unknown } | null
  actualDuration?: number
  actualStartTime?: number
}

const PROFILER_TAG = 12
const TOP_N = 12

function fiberOf(element: Element): Fiber | null {
  const key = Object.keys(element).find((k) => k.startsWith('__reactFiber$'))
  return key ? ((element as unknown as Record<string, Fiber>)[key] ?? null) : null
}

function componentFn(fiber: Fiber): ((...args: unknown[]) => unknown) | null {
  const t = fiber.type
  if (typeof t === 'function') return t as (...args: unknown[]) => unknown
  if (t && typeof t === 'object') {
    // React.memo(fn) / forwardRef(fn)
    const inner =
      (t as { type?: unknown; render?: unknown }).type ?? (t as { render?: unknown }).render
    if (typeof inner === 'function') return inner as (...args: unknown[]) => unknown
  }
  return null
}

/** The fiber (current or alternate) carrying the most recent render. */
function newest(fiber: Fiber): Fiber {
  const alt = fiber.alternate
  if (!alt) return fiber
  return (alt.actualStartTime ?? -1) > (fiber.actualStartTime ?? -1) ? alt : fiber
}

function selfTime(fiber: Fiber): number {
  let children = 0
  for (let c = fiber.child; c; c = c.sibling) children += c.actualDuration ?? 0
  return Math.max(0, (fiber.actualDuration ?? 0) - children)
}

function snippetOf(fn: (...args: unknown[]) => unknown): string {
  try {
    return fn.toString().replace(/\s+/g, ' ').slice(0, 90)
  } catch {
    return ''
  }
}

/** Walk up from the widget's content element to its own <Profiler id=widgetId>. */
function profilerFiber(element: Element, widgetId: string): Fiber | null {
  for (let f = fiberOf(element); f; f = f.return) {
    if (f.tag === PROFILER_TAG && f.memoizedProps?.id === widgetId) return f
  }
  return null
}

export function profileWidgetFibers(
  widgetId: string,
  contentElement: Element | null
): WidgetFiberProfile | null {
  if (!contentElement) return null
  try {
    const profiler = profilerFiber(contentElement, widgetId)
    if (!profiler) return null
    const root = newest(profiler)
    // Fibers that rendered in this commit share the Profiler's start window;
    // anything older carries a stale duration from a previous commit.
    const commitStart = root.actualStartTime ?? 0

    const byFn = new Map<(...args: unknown[]) => unknown, ComponentCost>()
    let fibers = 0
    const stack: Fiber[] = root.child ? [root.child] : []
    while (stack.length) {
      let f = stack.pop() ?? null
      while (f) {
        fibers++
        if (f.child) stack.push(f.child)
        const fn = componentFn(f)
        if (fn) {
          let cost = byFn.get(fn)
          if (!cost) {
            cost = {
              name: fn.name || 'anonymous',
              snippet: snippetOf(fn),
              instances: 0,
              inCommit: 0,
              selfMs: 0,
              maxSelfMs: 0,
            }
            byFn.set(fn, cost)
          }
          cost.instances++
          const latest = newest(f)
          if ((latest.actualStartTime ?? -1) >= commitStart) {
            const self = selfTime(latest)
            cost.inCommit++
            cost.selfMs += self
            if (self > cost.maxSelfMs) cost.maxSelfMs = self
          }
        }
        f = f.sibling
      }
    }

    const top = Array.from(byFn.values())
      .filter((c) => c.inCommit > 0)
      .sort((a, b) => b.selfMs - a.selfMs)
      .slice(0, TOP_N)
      .map((c) => ({
        ...c,
        selfMs: Math.round(c.selfMs * 100) / 100,
        maxSelfMs: Math.round(c.maxSelfMs * 100) / 100,
      }))

    return {
      widgetId,
      fibers,
      commitMs: Math.round((root.actualDuration ?? 0) * 100) / 100,
      top,
    }
  } catch {
    return null
  }
}
